import React, { ChangeEvent } from 'react';
import { Drawer, DrawerProps } from '@mui/material';
import {
  MyMenuContentWrapper,
  MyMenuHeader,
} from '@/components/MenuDrawer/MyMenuDrawer/styles';
import Switch from '@/components/@IntranetLibrary/Switch';
import ContentDescription, {
  ContentDivider,
} from '@/components/MenuDrawer/MyMenuDrawer/@components/ContentDescription';
import {
  ItemContent,
  ItemTitle,
  ItemWrapper,
  RadioSelect,
  RadioSelectWrapper,
  Section,
  SectionContent,
  SectionTitle,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/AlarmSetting/Approval/style';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useRouter } from 'next/router';
import { useMypageEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { MypageEventName } from '@/constant/gtm';
import { PAGE_URL } from '@/constant/url';
import { useTranslation } from 'react-i18next';

export type ApprovalAlarmSettingProps = {
  readonly isApprovalOpen: boolean;
  readonly approvalOnBack: () => void;
  readonly approvalOnClose: () => void;
  readonly onBackDropClick: DrawerProps['onClose'];
  readonly isLoading: boolean;
  readonly handleChangeAlarm: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  readonly alarmOptionOnChange: (
    event: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => void;
  readonly approvalValues: {
    readonly all: boolean;
    readonly approval_opinion_attach: boolean;
    readonly approval_hold: boolean;
    readonly approval_confirm: boolean;
    readonly approval_reject: boolean;
    readonly approval_process: boolean;
    readonly approval_reject_request: boolean;
    readonly approval_add_referee: boolean;
    readonly approval_cancel: boolean;
    readonly approval_cancel_referee: boolean;
    readonly approval_delay_reminder: boolean;
    readonly approval_confirm_referee: boolean;
    readonly approval_confirm_annual_leave_occurred: boolean;
    readonly approval_confirm_working_hours_changed: boolean;
    readonly approval_confirm_overtime_occurred: boolean;
    readonly approval_confirm_commute_adjusted: boolean;
    readonly approval_hold_value: ApprovalRadioValue;
    readonly approval_confirm_value: ApprovalRadioValue;
    readonly approval_add_referee_value: ApprovalRadioValue;
    readonly approval_confirm_referee_value: ApprovalRadioValue;
  };
};

type ApprovalRadioValue = 'always' | 'by_opinion';
const ApprovalRadioValueLabel: { [key in ApprovalRadioValue]: string } = {
  always: '항상 받기',
  by_opinion: '의견 있을 때만 받기',
};

const ApprovalAlarmSetting = ({
  isApprovalOpen,
  approvalOnBack,
  approvalOnClose,
  onBackDropClick,
  handleChangeAlarm,
  alarmOptionOnChange,
  isLoading,
  approvalValues,
}: ApprovalAlarmSettingProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const isMobile = router.route.startsWith(PAGE_URL.HOME_MOBILE);
  const mypageEventTracker = useMypageEventTracker();

  const onClickSwitch = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    contents: string,
  ) => {
    handleChangeAlarm(event, checked);

    // GA Mypage - 22번 - 스위치
    mypageEventTracker(MypageEventName.clickEvent, {
      area_name: '알림 설정 > APPROVAL',
      contents,
      status: checked,
      description: '알람 설정 토글',
    });
  };

  const onClickRadio = (
    event: ChangeEvent<HTMLInputElement>,
    value: string,
    area: string,
  ) => {
    alarmOptionOnChange(event, value);

    // GA Mypage - 22번 - 라디오
    mypageEventTracker(MypageEventName.clickEvent, {
      area_name: `알림 설정 > APPROVAL > ${area}`,
      contents: ApprovalRadioValueLabel[value as ApprovalRadioValue] || value,
      status: true,
      description: '알람 설정 토글',
    });
  };

  const ApprovalRadioValues = Object.entries(ApprovalRadioValueLabel).map(
    ([value, label]) => ({
      label: t(`MYPAGE.${label}`),
      value: value as ApprovalRadioValue,
    }),
  );

  return (
    <Drawer
      key={'AlarmApprovalSetting'}
      id={'AlarmApprovalSetting'}
      open={isApprovalOpen}
      onClose={onBackDropClick}
      anchor={'right'}
      ModalProps={{
        sx: { '.MuiModal-backdrop': { opacity: '0 !important' } },
      }}
      PaperProps={{
        sx: { width: isMobile ? '100vw' : '400px' },
      }}
    >
      <MyMenuHeader
        onBack={approvalOnBack}
        onClose={approvalOnClose}
        hasBackButton={!isMobile}
        title={'APPROVAL'}
      />
      <MyMenuContentWrapper hasSidePadding={true}>
        <ContentDivider gap={'16px'}>
          <ContentDescription
            hasVerticalMargin={false}
            sx={{ margin: `28px 0 ${28 - 16}px`, padding: '16px 12px' }}
          >
            <FormControlLabel
              sx={{ padding: 0, margin: 0 }}
              control={
                <ItemWrapper>
                  <ItemTitle>{t('MYPAGE.모두 켜기')}</ItemTitle>
                  <ItemContent>
                    <Switch
                      onChange={(...props) =>
                        onClickSwitch(...props, '모두 켜기')
                      }
                      disabled={isLoading}
                      name={'all'}
                      checked={approvalValues['all']}
                    />
                  </ItemContent>
                </ItemWrapper>
              }
              label={''}
            />
          </ContentDescription>

          <Section>
            <SectionTitle>{t('MYPAGE.내가 작성자인 문서')}</SectionTitle>
            <SectionContent>
              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>
                      {t('MYPAGE.중간에 의견이 추가됐을 때')}
                    </ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '중간에 의견이 추가됐을 때')
                        }
                        disabled={isLoading}
                        name={'approval_opinion_attach'}
                        checked={approvalValues['approval_opinion_attach']}
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />
              <RadioSelectWrapper>
                <ItemWrapper>
                  <ItemTitle>{t('MYPAGE.보류됐을 때')}</ItemTitle>
                  <ItemContent>
                    <Switch
                      onChange={(...props) =>
                        onClickSwitch(...props, '보류됐을 때')
                      }
                      disabled={isLoading}
                      name={'approval_hold'}
                      checked={approvalValues['approval_hold']}
                    />
                  </ItemContent>
                </ItemWrapper>
                <RadioSelect
                  name={'approval_hold'}
                  value={approvalValues['approval_hold_value']}
                  disabled={isLoading || !approvalValues['approval_hold']}
                  options={ApprovalRadioValues}
                  onChange={(...props) => onClickRadio(...props, '보류됐을 때')}
                />
              </RadioSelectWrapper>
              <RadioSelectWrapper>
                <FormControlLabel
                  sx={{ padding: 0, margin: 0 }}
                  control={
                    <ItemWrapper>
                      <ItemTitle>{t('MYPAGE.최종 승인됐을 때')}</ItemTitle>
                      <ItemContent>
                        <Switch
                          onChange={(...props) =>
                            onClickSwitch(...props, '최종 승인됐을 때')
                          }
                          disabled={isLoading}
                          name={'approval_confirm'}
                          checked={approvalValues['approval_confirm']}
                        />
                      </ItemContent>
                    </ItemWrapper>
                  }
                  label={''}
                />
                <RadioSelect
                  name={'approval_confirm'}
                  value={approvalValues['approval_confirm_value']}
                  disabled={isLoading || !approvalValues['approval_confirm']}
                  options={ApprovalRadioValues}
                  onChange={(...props) =>
                    onClickRadio(...props, '최종 승인됐을 때')
                  }
                />
              </RadioSelectWrapper>
              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>{t('MYPAGE.반려됐을 때')}</ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '반려됐을 때')
                        }
                        disabled={isLoading}
                        name={'approval_reject'}
                        checked={approvalValues['approval_reject']}
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>{t('MYPAGE.내가 결재자인 문서')}</SectionTitle>
            <SectionContent>
              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>{t('MYPAGE.결재할 차례일 때')}</ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '결제할 차례일 때')
                        }
                        disabled={isLoading}
                        name={'approval_process'}
                        checked={approvalValues['approval_process']}
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />

              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>{t('MYPAGE.반려 요청이 왔을 때')}</ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '반려 요청이 왔을 때')
                        }
                        disabled={isLoading}
                        name={'approval_reject_request'}
                        checked={approvalValues['approval_reject_request']}
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />

              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>{t('MYPAGE.품의가 취소됐을 때')}</ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '품의가 취소됐을 때')
                        }
                        disabled={isLoading}
                        name={'approval_cancel'}
                        checked={approvalValues['approval_cancel']}
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />

              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>{t('MYPAGE.결재가 지연됐을 때')}</ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '결재가 지연됐을 때')
                        }
                        disabled={isLoading}
                        name={'approval_delay_reminder'}
                        checked={approvalValues['approval_delay_reminder']}
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>{t('MYPAGE.내가 참조자인 문서')}</SectionTitle>
            <SectionContent>
              <RadioSelectWrapper>
                <FormControlLabel
                  sx={{ padding: 0, margin: 0 }}
                  control={
                    <ItemWrapper>
                      <ItemTitle>
                        {t('MYPAGE.참조인으로 추가됐을 때')}
                      </ItemTitle>
                      <ItemContent>
                        <Switch
                          onChange={(...props) =>
                            onClickSwitch(...props, '참조인으로 추가됐을 때')
                          }
                          disabled={isLoading}
                          name={'approval_add_referee'}
                          checked={approvalValues['approval_add_referee']}
                        />
                      </ItemContent>
                    </ItemWrapper>
                  }
                  label={''}
                />
                <RadioSelect
                  name={'approval_add_referee'}
                  value={approvalValues['approval_add_referee_value']}
                  disabled={
                    isLoading || !approvalValues['approval_add_referee']
                  }
                  options={ApprovalRadioValues}
                  onChange={(...props) =>
                    onClickRadio(...props, '참조인으로 추가됐을 때')
                  }
                />
              </RadioSelectWrapper>
              <RadioSelectWrapper>
                <FormControlLabel
                  sx={{ padding: 0, margin: 0 }}
                  control={
                    <ItemWrapper>
                      <ItemTitle>{t('MYPAGE.최종 승인됐을 때')}</ItemTitle>
                      <ItemContent>
                        <Switch
                          onChange={(...props) =>
                            onClickSwitch(
                              ...props,
                              '참조 문서 최종 승인됐을 때',
                            )
                          }
                          disabled={isLoading}
                          name={'approval_confirm_referee'}
                          checked={approvalValues['approval_confirm_referee']}
                        />
                      </ItemContent>
                    </ItemWrapper>
                  }
                  label={''}
                />
                <RadioSelect
                  name={'approval_confirm_referee'}
                  value={approvalValues['approval_confirm_referee_value']}
                  disabled={
                    isLoading || !approvalValues['approval_confirm_referee']
                  }
                  options={ApprovalRadioValues}
                  onChange={(...props) =>
                    onClickRadio(...props, '참조 문서 최종 승인됐을 때')
                  }
                />
              </RadioSelectWrapper>

              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>{t('MYPAGE.품의가 취소됐을 때')}</ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '참조 문서 취소됐을 때')
                        }
                        disabled={isLoading}
                        name={'approval_cancel_referee'}
                        checked={approvalValues['approval_cancel_referee']}
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>{t('MYPAGE.팀원의 근태 문서')}</SectionTitle>
            <SectionContent>
              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>{t('MYPAGE.근무시간이 변경됐을 때')}</ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '근무시간이 변경됐을 때')
                        }
                        disabled={isLoading}
                        name={'approval_confirm_working_hours_changed'}
                        checked={
                          approvalValues[
                            'approval_confirm_working_hours_changed'
                          ]
                        }
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />
              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>{t('MYPAGE.연차가 발생됐을 때')}</ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '연차가 발생됐을 때')
                        }
                        disabled={isLoading}
                        name={'approval_confirm_annual_leave_occurred'}
                        checked={
                          approvalValues[
                            'approval_confirm_annual_leave_occurred'
                          ]
                        }
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />
              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>{t('MYPAGE.연장근무가 발생됐을 때')}</ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(...props, '연장근무가 발생됐을 때')
                        }
                        disabled={isLoading}
                        name={'approval_confirm_overtime_occurred'}
                        checked={
                          approvalValues['approval_confirm_overtime_occurred']
                        }
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />
              <FormControlLabel
                sx={{ padding: 0, margin: 0 }}
                control={
                  <ItemWrapper>
                    <ItemTitle>
                      {t('MYPAGE.외출/출근/퇴근 조정이 발생됐을 때')}
                    </ItemTitle>
                    <ItemContent>
                      <Switch
                        onChange={(...props) =>
                          onClickSwitch(
                            ...props,
                            '외출/출근/퇴근 조정이 발생됐을 때',
                          )
                        }
                        disabled={isLoading}
                        name={'approval_confirm_commute_adjusted'}
                        checked={
                          approvalValues['approval_confirm_commute_adjusted']
                        }
                      />
                    </ItemContent>
                  </ItemWrapper>
                }
                label={''}
              />
            </SectionContent>
          </Section>
        </ContentDivider>
      </MyMenuContentWrapper>
    </Drawer>
  );
};

export default ApprovalAlarmSetting;
